import {
  WSEmptyState,
  WSTable,
  WSTableCell,
  WSTableColumn,
  WSTableItem,
  WSTableRowMenuAction
} from "@wingspanhq/fe-component-library";
import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces";
import React, { useMemo } from "react";
import { useQueryPayerRows } from "../../../query/search/payer/queries/useQueryPayerRows";
import { getPayerName } from "../../Payers/utils/getPayerNames";
import {
  ExtendedRequirementTableItemData,
  RequirementAdditionalData,
  RequirementContextType,
  RequirementTableItemData
} from "../types";
import { useRequirementsConfig } from "../useRequirementsConfig";
import {
  getIsCustomRequirement,
  getIsGeneralRequirement,
  getRequirementListKey
} from "../utils";

export type RequirementsTableProps = {
  requirements: Array<ExtendedRequirementTableItemData>;
  contextType: RequirementContextType;
  hideActions?: boolean;
  compact?: boolean;
  showOverrideActions?: boolean;
  hideTaxVerificationConsent?: boolean;
  requiredAction?: string;
  getOnViewDetails: (
    item: IRequirementResponse,
    additionalData?: RequirementAdditionalData
  ) => ((item: IRequirementResponse) => void) | undefined;
};

export const ExtendedRequirementsTable: React.FC<RequirementsTableProps> = ({
  requirements,
  contextType,
  hideActions,
  compact,
  showOverrideActions,
  hideTaxVerificationConsent,
  getOnViewDetails,
  requiredAction
}) => {
  const requirementsConfig = useRequirementsConfig({
    requirements: requirements.filter(
      requirement => "requirementId" in requirement
    ) as RequirementTableItemData[],
    contextType,
    context: {
      hideTaxVerificationConsent,
      showOverrideActions
    }
  });

  const tableData: WSTableItem<ExtendedRequirementTableItemData>[] =
    requirements.map((requirement, index) => {
      return {
        id: getRequirementListKey(requirement, index),
        data: requirement
      };
    });

  const columns: Array<WSTableColumn<ExtendedRequirementTableItemData>> = [
    {
      config: {
        header: {
          text: "Requirement"
        },
        gridTemplateSizeMax: "0.5fr"
      },
      renderFunction: rowItem => {
        if (getIsCustomRequirement(rowItem.data)) {
          return <WSTableCell {...rowItem.data.nameCell} />;
        }

        return (
          <WSTableCell {...requirementsConfig.getNameCellProps(rowItem.data)} />
        );
      }
    },
    {
      config: {
        header: {
          text: "Type"
        },
        hideOnScreens: compact ? ["XS", "S", "M", "L", "XL"] : ["XS"],
        gridTemplateSizeMax: "0.5fr"
      },
      renderFunction: rowItem => {
        if (getIsCustomRequirement(rowItem.data)) {
          return <WSTableCell {...rowItem.data.typeCell} />;
        }

        return (
          <WSTableCell {...requirementsConfig.getTypeCellProps(rowItem.data)} />
        );
      }
    },
    {
      config: {
        hideOnScreens:
          contextType === RequirementContextType.Payee
            ? ["XS", "S", "M", "L", "XL"]
            : [],
        header: {
          text: "Clients"
        },
        gridTemplateSizeMax: "0.4fr"
      },
      renderFunction: rowItem => {
        if (getIsCustomRequirement(rowItem.data) && rowItem.data.clientsCell) {
          return <WSTableCell {...rowItem.data.clientsCell} />;
        }

        return (
          <ClientsCell
            payerId={rowItem.data.payerId}
            payerPayeeEngagementIds={rowItem.data.payerPayeeEngagementIds}
          />
        );
      }
    },
    {
      config: {
        hideOnScreens:
          contextType === RequirementContextType.Payer
            ? ["XS", "S", "M", "L", "XL"]
            : [],
        header: {
          text: "Engagements"
        },
        gridTemplateSizeMax: "0.4fr"
      },
      renderFunction: rowItem => {
        if (getIsCustomRequirement(rowItem.data)) {
          return <WSTableCell {...rowItem.data.engagementsCell} />;
        }

        return (
          <WSTableCell
            text={
              rowItem.data.payerPayeeEngagementIds?.length?.toString() || "-"
            }
          />
        );
      }
    },
    {
      config: {
        header: {
          text: "Status"
        },
        gridTemplateSizeMax: "0.5fr"
      },
      renderFunction: rowItem => {
        if (getIsCustomRequirement(rowItem.data)) {
          return <WSTableCell {...rowItem.data.statusCell} />;
        }

        return (
          <WSTableCell
            {...requirementsConfig.getStatusCellProps(rowItem.data)}
          />
        );
      }
    }
  ];

  if (requirements.length === 0) {
    return (
      <WSEmptyState
        type="tasks"
        title="None required"
        description="Nothing required for this payee"
        hasBorder
        orientation="horizontal"
      />
    );
  }

  return (
    <WSTable<ExtendedRequirementTableItemData>
      loading={requirementsConfig.isLoading}
      key={requirementsConfig.dataUpdatedAt}
      getOnRowClick={({ data }) => {
        const requirement = data;

        const hasViewDetails =
          !getIsCustomRequirement(requirement) &&
          !getIsGeneralRequirement(requirement);

        if (!hasViewDetails) {
          return undefined;
        }

        const additionalData =
          requirementsConfig.getAdditionalData(requirement);
        const callback = getOnViewDetails(requirement, additionalData);

        return callback
          ? ({ data }) => callback(data as IRequirementResponse)
          : undefined;
      }}
      rowActions={({ data }) => {
        if (getIsCustomRequirement(data)) {
          return [];
        }

        if (hideActions || !requiredAction) {
          return [];
        }

        const isActionRequired = requirementsConfig.isActionRequired(data);

        if (isActionRequired) {
          return [
            {
              text: requiredAction,
              onClick: () => {
                const additionalData =
                  requirementsConfig.getAdditionalData(data);
                const callback = getOnViewDetails(
                  data as IRequirementResponse,
                  additionalData
                );

                return callback && callback(data as IRequirementResponse);
              }
            }
          ];
        }

        return [];
      }}
      rowMenuActions={({ data }) => {
        if (getIsCustomRequirement(data)) {
          return data.menuActions || [];
        }
        if (hideActions) {
          return [];
        }

        const veiwDetails: WSTableRowMenuAction<RequirementTableItemData> = {
          label: "View requirement",
          icon: "view",
          onClick: () => {
            const additionalData = requirementsConfig.getAdditionalData(data);

            const callback = getOnViewDetails(
              data as IRequirementResponse,
              additionalData
            );

            return callback && callback(data as IRequirementResponse);
          }
        };

        const requirement = data as IRequirementResponse;

        const hasViewDetails =
          !getIsCustomRequirement(data) && !!getOnViewDetails(data);

        if (hasViewDetails && !getIsGeneralRequirement(requirement)) {
          return [
            veiwDetails,
            ...requirementsConfig.renderRowMenuActions(data)
          ];
        }

        return requirementsConfig.renderRowMenuActions(data);
      }}
      columns={columns}
      tableData={tableData}
      data-testid="requirementsTable"
    />
  );
};

const ClientsCell: React.FC<{
  payerId: string;
  payerPayeeEngagementIds?: string[];
}> = props => {
  const queryPayers = useQueryPayerRows({
    filter: {
      payerId: props.payerId
    }
  });

  const payerName = useMemo(() => {
    if (!queryPayers.data?.[0]) {
      return;
    }

    return getPayerName(queryPayers.data[0]);
  }, [queryPayers.data]);

  return (
    <WSTableCell
      text={payerName}
      secondaryText={
        !!props.payerPayeeEngagementIds?.length
          ? `${props.payerPayeeEngagementIds.length} engagement(s)`
          : undefined
      }
    />
  );
};
